import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import logoImg from '@/assets/logo.png';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const redirectTo = (location.state as any)?.from || '/dashboard';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate(redirectTo);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — navy branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0D1B2A] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-[#00C2C7]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-[#00C2C7]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10">
          <img src={logoImg} alt="TalentumIQ" className="h-10 object-contain" />
        </div>

        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-bold text-white leading-tight font-heading">
            Contrate as pessoas <span className="text-[#00C2C7]">certas</span><br />
            na metade do tempo.
          </h1>
          <p className="text-gray-400 text-lg max-w-md leading-relaxed">
            IA para analisar currículos, pontuar candidatos e conduzir entrevistas — decisões com dados, não com instinto.
          </p>

          {/* Floating card preview */}
          <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-white text-sm font-semibold font-heading">Top Candidatos</span>
              <span className="text-[#00C2C7] text-xs font-semibold bg-[#00C2C7]/10 px-2.5 py-0.5 rounded-full">IA ativa</span>
            </div>
            <div className="space-y-3">
              {[
                { initials: 'MR', name: 'Marina Rocha', role: 'Node.js · AWS', score: 94, color: '#00C2C7' },
                { initials: 'FC', name: 'Felipe Costa', role: 'Python · GCP', score: 87, color: '#06D6A0' },
                { initials: 'JA', name: 'Julia Alves', role: 'Java · Azure', score: 79, color: '#FFD166' },
              ].map((c) => (
                <div key={c.initials} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-[#0D1B2A]" style={{ background: c.color }}>
                    {c.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium">{c.name}</p>
                    <p className="text-gray-500 text-xs">{c.role}</p>
                  </div>
                  <span className="text-sm font-bold" style={{ color: c.color }}>{c.score}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="relative z-10 text-gray-600 text-sm">
          © {new Date().getFullYear()} TalentumIQ · Powered by Druid
        </p>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <img src={logoImg} alt="TalentumIQ" className="h-12 object-contain mx-auto mb-2" />
            <p className="text-muted-foreground">Recrutamento inteligente, decisões precisas</p>
          </div>

          <div className="hidden lg:block text-center mb-8">
            <h2 className="text-2xl font-bold font-heading text-foreground">Bem-vindo de volta</h2>
            <p className="text-muted-foreground mt-1">Acesse sua conta para continuar</p>
          </div>

          <Card className="shadow-lg border-0 shadow-black/5">
            <CardHeader className="space-y-1 lg:hidden">
              <CardTitle className="text-xl text-center font-heading">Entrar</CardTitle>
              <CardDescription className="text-center">
                Acesse sua conta para continuar
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">Email</label>
                  <Input
                    id="email" type="email" placeholder="seu@email.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium">Senha</label>
                  <Input
                    id="password" type="password" placeholder="••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required disabled={isLoading}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>
              <div className="mt-4 text-center text-sm text-muted-foreground">
                <a href="/forgot-password" className="hover:text-primary">Esqueceu sua senha?</a>
              </div>
              <div className="mt-2 text-center text-sm text-muted-foreground">
                Não tem uma conta?{' '}
                <a href="/signup" className="text-primary hover:underline font-medium">Criar conta</a>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6 lg:hidden">
            Powered by <span className="font-medium">Druid</span> · © {new Date().getFullYear()} TalentumIQ
          </p>
        </div>
      </div>
    </div>
  );
}
